import type { FormInstance } from "antd";
import type { SourcingRequirementApplicationFormFields } from "../type";
import { useEffect } from "react";
import dayjs from "dayjs";
import { convertFileItems } from "@/utils/file";
import { useUserFormFieldsQuery } from "@/hooks/services";
import { SrmSourcingApplicationItemDetail } from "@/types/apis";

export function useInitFormFields(
  form: FormInstance<SourcingRequirementApplicationFormFields>,
  open: boolean,
  detail?: SrmSourcingApplicationItemDetail,
) {
  const userInfo = useUserFormFieldsQuery(detail?.createBy);

  useEffect(() => {
    if (!open) return;

    if (detail) {
      form.setFieldsValue({
        reviewStatus: detail.reviewStatus.toString(),
        factoryValue: detail.factoryValue,
        costCenter: detail.costCenter,
        sourcingType: detail.sourcingType,
        subSourcingType: detail.subSourcingType,
        coaFiles: convertFileItems(detail.coaFiles),
        ursFiles: convertFileItems(detail.ursFiles),
        othersFiles: convertFileItems(detail.othersFiles),
        standardMaterialFiles: convertFileItems(detail.standardMaterialFiles),
        materialList: detail.srmSourcingMaterialList.map((m) => {
          return {
            id: m.id,
            materialCode: m.materialCode,
            materialName: m.materialName,
            count: m.annualQuantity,
            products: m.relatedProducts,
            completeDate: m.completionDate ? dayjs(m.completionDate) : null,
            sourcingReason: m.sourcingReason,
            sourcingRequirements: m.sourcingRequirements,
            sourcingPrioritize: m.sourcingPrioritize,
            isSourcingCancel:
              m.sourcingStatus === "3" || m.sourcingStatus === "4",
            cancelReason: m.cancelReason,
            sourcingNodeList: m.sourcingNodeList,
            sourcingMethod: m.sourcingMethod,
            sourcingOrigin: m.sourcingOrigin,
            planStartTime: m.plannedStartTime
              ? dayjs(m.plannedStartTime)
              : null,
            planEndTime: m.plannedEndTime ? dayjs(m.plannedEndTime) : null,
          };
        }),
        formulaChange: detail.formulaChange,
        changedTime: detail.changedTime && dayjs(detail.changedTime),
        standardChanged: detail.standardChanged,
        newStandardFiles: convertFileItems(detail.newStandardFiles),
      });
    }

    if (userInfo) {
      form.setFieldsValue({
        username: userInfo.nickname,
        createDate: detail?.createTime
          ? dayjs(detail.createTime).format("YYYY-MM-DD")
          : dayjs().format("YYYY-MM-DD"),
        employeeCode: userInfo.username,
        department: userInfo.deptName,
        postName: userInfo.postName,
        email: userInfo.email,
      });
    }
  }, [detail, open, form, userInfo]);

  return {
    detail,
  };
}
